import {
  User,
  UserList,
  QueryUserArgs as QueryUserArgsBase,
  MutationUpdateUserArgs as MutationUpdateUserArgsBase,
  MutationCreateUserArgs as MutationCreateUserArgsBase,
  MutationActivateUserArgs,
  MutationDeactivateUserArgs,
  UpdateUserByEmailInput,
  EmailChange,
  MutationUpdateUserPhoneNumberArgs,
  PhoneNumber
} from "@irati/types"
import client, {MutationOptions, QueryOptions} from "@irati/utils/services/apollo"

import PBService from "@irati/utils/services"
import setIfNotSet from "@irati/utils/setIfNotSet"

import authentication from "@irati/auth"
import regionErrorHandler from "./errors/regions"
import userErrorHandler from "./errors/legacy/userErrorHandler"
import updateUserMutation, {updateUserKey} from "./mutations/updateUser"
import createUserMutation, {createUserKey} from "./mutations/createUser"
import createUserForBulkMutation, {createUserForBulkKey} from "./mutations/createUserForBulk"
import getUserQuery, {getUserKey} from "./queries/getUser"
import getActivatedAtQuery, {getActivatedAtKey} from "./queries/getActivatedAt"
import getUsersForBusiness, {QueryUsersArgs as QueryUsersForBusinessArgs} from "./queries/usersForBusiness"
import getNonOnboardedUsersForBusiness, {nonOnboardedUsersForBusinessKey} from "./queries/nonOnboardedUsersForBusiness"
import getUserByEmailQuery from "./queries/getUserByEmail"
import getUserIdByEmailQuery from "./queries/getUserIdByEmail"
import updateUsersByEmailMutation, {updateUsersByEmailKey} from "./mutations/updateUsersByEmail"
import makeUserGlobalAdminMutation, {makeUserGlobalAdminKey} from "./mutations/makeUserGlobalAdmin"
import makeUserBasicMutation, {makeUserBasicKey} from "./mutations/makeUserBasic"
import activateUserMutation from "./mutations/activateUser"
import deactivateUserMutation from "./mutations/deactivateUser"
import unAuthedUserQuery, {unAuthedUserKey} from "./queries/unAuthedUser"
import bulkChangeEmailMutation from "./mutations/bulkChangeEmail"
import fullUser from "./fragments/fullUserV4"
import userRegionIdQuery, {userRegionIdKey} from "./queries/userRegionId"
import getUserVerifiedPhoneNoQuery, {getUserPhoneNumberKey} from "./queries/getUserVerifiedPhoneNo"
import updateUserVerifiedPhoneNoMutation from "./mutations/updateUserVerifiedPhoneNo"
import deleteUserVerifiedPhoneNoMutation from "./mutations/deleteUserVerifiedPhoneNo"

interface withTeams {
  withTeams?: boolean
}

interface withPreferences {
  withPreferences?: boolean
}

interface MutationCreateUserArgs extends MutationCreateUserArgsBase, withTeams {}
interface MutationUpdateUserArgs extends MutationUpdateUserArgsBase, withTeams, withPreferences {}
export interface QueryUserArgs extends QueryUserArgsBase, withTeams, withPreferences {}

class UserService extends PBService {
  get(
    userId: string,
    {withTeams = false, withPreferences = false}: Omit<QueryUserArgs, "id"> = {},
    options: QueryOptions = {}
  ): Promise<User> {
    const variables = {id: userId, withTeams, withPreferences}
    return client.query({...options, query: getUserQuery, variables}).then(({data}) => data[getUserKey])
  }

  getActivatedAt(userId: string, options: QueryOptions = {}): Promise<string> {
    const variables = {id: userId}
    return client.query({...options, query: getActivatedAtQuery, variables}).then(({data}) => data[getActivatedAtKey].activatedAt)
  }

  getUserByEmail(email: string, tenantId: string = authentication.userTenant().uuid, options: QueryOptions = {}): Promise<User | null> {
    const variables = {email, tenantId}
    return client.query({query: getUserByEmailQuery, variables, ...options}).then((res) => res.data.getUserByEmail)
  }

  getUserIdByEmail(email: string, tenantId: string = authentication.business().uuid): Promise<string | null> {
    const variables = {email, tenantId}
    return client.query({query: getUserIdByEmailQuery, variables}).then((res) => res.data.getUserByEmail)
  }

  getUserVerifiedPhoneNo(userId: string, options: QueryOptions = {}): Promise<PhoneNumber> {
    const variables = {userId}
    return client.query({query: getUserVerifiedPhoneNoQuery, variables, ...options}).then((res) => res.data[getUserPhoneNumberKey])
  }

  deleteUserPhoneNo(userId: string, options: QueryOptions = {}): Promise<PhoneNumber> {
    const variables = {userId}
    return client.mutate({mutation: deleteUserVerifiedPhoneNoMutation, variables, ...options}).then(() => true)
  }

  unAuthedUser(uuid: string, options: QueryOptions = {}): Promise<User> {
    const variables = {id: uuid}
    return client
      .query({...options, metadata: {unAuth: true}, query: unAuthedUserQuery, variables})
      .then((res) => res.data[unAuthedUserKey])
  }

  getUsersForBusiness(variables: QueryUsersForBusinessArgs, options: QueryOptions = {}): Promise<UserList> {
    setIfNotSet(variables, "includeBusiness", false)
    setIfNotSet(variables, "includeIntegrations", false)
    setIfNotSet(variables, "includeTeams", false)
    setIfNotSet(variables, "businessId", () => authentication.business().uuid)
    return client.query({...options, query: getUsersForBusiness, variables}).then(({data}) => {
      return data.usersForBusiness
    })
  }

  getNonOnboardedUsersForBusiness(variables: QueryUsersForBusinessArgs, options: QueryOptions = {}): Promise<UserList> {
    setIfNotSet(variables, "nonOnboarded", true)
    setIfNotSet(variables, "businessId", () => authentication.business().uuid)
    return client.query({...options, query: getNonOnboardedUsersForBusiness, variables}).then(({data}) => {
      return data[nonOnboardedUsersForBusinessKey]
    })
  }

  create({withTeams = false, ...args}: MutationCreateUserArgs, options: MutationOptions = {}): Promise<User> {
    const variables: MutationCreateUserArgs = {
      user: args.user,
      withTeams
    }

    return client.mutate({...options, mutation: createUserMutation, variables}).then((res) => res.data[createUserKey])
  }

  createUserForBulk(args: MutationCreateUserArgs, options: MutationOptions = {}): Promise<{uuid: string}> {
    const variables: MutationCreateUserArgs = {user: args.user}

    return client.mutate({...options, mutation: createUserForBulkMutation, variables}).then((res) => res.data[createUserForBulkKey])
  }

  update({withTeams = false, withPreferences = false, ...args}: MutationUpdateUserArgs, options: MutationOptions = {}): Promise<User> {
    const variables: MutationUpdateUserArgs = {
      user: args.user,
      withTeams,
      withPreferences
    }

    return client.mutate({...options, mutation: updateUserMutation, variables}).then((res) => res.data[updateUserKey])
  }

  async updateUserVerifiedPhoneNo(variables: MutationUpdateUserPhoneNumberArgs, options: MutationOptions = {}): Promise<boolean> {
    await client.mutate({...options, mutation: updateUserVerifiedPhoneNoMutation, variables})
    return true
  }

  updateUsersByEmail(users: UpdateUserByEmailInput[], tenantId: string = authentication.userTenant().uuid): Promise<User[]> {
    const variables = {users, tenantId}
    return client.mutate({mutation: updateUsersByEmailMutation, variables}).then((res) => {
      return res.data[updateUsersByEmailKey]
    })
  }

  activate(user: User, options: QueryOptions = {}): Promise<boolean> {
    const variables: MutationActivateUserArgs = {
      userId: user.uuid
    }

    return client.mutate({...options, mutation: activateUserMutation, variables}).then(() => {
      patchUserToCache(user.uuid, {isEnabled: true})
      return true
    })
  }

  deactivate(user: User, options: QueryOptions = {}): Promise<boolean> {
    const variables: MutationDeactivateUserArgs = {
      userId: user.uuid
    }

    return client.mutate({...options, mutation: deactivateUserMutation, variables}).then(() => {
      patchUserToCache(user.uuid, {isEnabled: false})
      return true
    })
  }

  bulkChangeEmail(businessId: string, changes: EmailChange[], options: MutationOptions = {}) {
    const variables = {
      businessId,
      changes
    }
    return client.mutate({...options, mutation: bulkChangeEmailMutation, variables}).then(() => true)
  }

  regionIdForUser(userId: string, options: QueryOptions = {}) {
    const variables = {
      id: userId
    }

    return client.query({...options, query: userRegionIdQuery, variables}).then(({data}) => data[userRegionIdKey])
  }

  /** @deprecated: Used for IDv2 => IDv4 role mapping, use 'addUserToTeam' */
  makeUserBasic(teamId: string, userId: string, options: MutationOptions = {}) {
    const variables = {teamId, userId}
    return client.mutate({...options, mutation: makeUserBasicMutation, variables}).then(({data}) => {
      patchUserToCache(userId, {isGlobalAdmin: false})
      return data[makeUserBasicKey]
    })
  }

  /** @deprecated: Used for IDv2 => IDv4 role mapping, use 'addGlobalAdminRole' */
  makeUserGlobalAdmin(userId: string, options: MutationOptions = {}) {
    const variables = {userId}
    return client.mutate({...options, mutation: makeUserGlobalAdminMutation, variables}).then(({data}) => {
      patchUserToCache(userId, {isGlobalAdmin: true})
      return data[makeUserGlobalAdminKey]
    })
  }
}

const usersService = new UserService({errorHandlers: [userErrorHandler, regionErrorHandler]})

export default usersService

function patchUserToCache(userId: string, data: Partial<User>) {
  const user = client.readFragment({id: `User@${userId}`, fragment: fullUser, fragmentName: "fullUser"})
  const updatedUser = {...user, ...data}
  client.writeFragment({id: `User@${userId}`, fragment: fullUser, fragmentName: "fullUser", data: updatedUser})
  return updatedUser
}
